import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * <ManifestSwitcher /> — Live-swaps the PWA manifest link based on hostname AND
 * URL path. This must render INSIDE <BrowserRouter> so it can react to route
 * changes (useLocation).
 *
 * Rules:
 *   • Hostname starts with `admin.`  → admin manifest, title "Zyro Admin"
 *   • Pathname starts with `/admin`  → admin manifest, title "Zyro Admin"
 *   • Otherwise                      → consumer manifest, title "Zyro"
 *
 * Because the admin manifest has `id: "/admin"` (distinct from the consumer
 * manifest's `id: "/"`), Chrome treats them as two separate installable PWAs
 * even on the same origin.
 */
export default function ManifestSwitcher() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const host = window.location.hostname;
    const isAdminHost = host.startsWith("admin.");
    const isAdminPath = location.pathname.startsWith("/admin");
    const useAdmin = isAdminHost || isAdminPath;

    // 1) Swap manifest <link>
    let linkEl = document.querySelector('link[rel="manifest"]');
    if (!linkEl) {
      linkEl = document.createElement("link");
      linkEl.rel = "manifest";
      document.head.appendChild(linkEl);
    }
    const nextHref = useAdmin ? "/admin-manifest.json" : "/manifest.json";
    if (linkEl.getAttribute("href") !== nextHref) {
      linkEl.setAttribute("href", nextHref);
    }

    // 2) Update theme-color so Chrome address bar matches the app identity
    let themeEl = document.querySelector('meta[name="theme-color"]');
    if (!themeEl) {
      themeEl = document.createElement("meta");
      themeEl.name = "theme-color";
      document.head.appendChild(themeEl);
    }
    themeEl.setAttribute("content", useAdmin ? "#0b1220" : "#111827");

    // 3) Apple mobile web app title
    let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitle) appleTitle.setAttribute("content", useAdmin ? "Zyro Admin" : "Zyro");

    // 4) Document title
    document.title = useAdmin ? "Zyro Admin — Control Panel" : "Zyro — Hotels & Rentals";

    // 5) Expose flag for downstream components
    document.documentElement.dataset.zyroApp = useAdmin ? "admin" : "consumer";
  }, [location.pathname]);

  return null;
}

export function isAdminContext() {
  if (typeof window === "undefined") return false;
  return (
    window.location.hostname.startsWith("admin.") ||
    window.location.pathname.startsWith("/admin")
  );
}
